import React from 'react';
import { RING } from '../../wall/wallTokens';

/**
 * The Portal page's furniture — the ring bar, the blurred backdrop, its buttons and icons.
 * Everything here is the viewer's vocabulary (#1A1A1A ground, #000 panels, 2px white rules,
 * radius 0), not the rest of the app's. Design handoff: Portal Page, Sept 2026.
 */

export const RED = '#FF2D2D';
export const GREEN = '#00FF7F';
export const CYAN = '#00FFFF';
export const MAGENTA = '#FF00AA';

/** Hover states invert over 200ms; the animations turn off under reduced motion. */
export const PORTAL_PAGE_CSS = `
  @keyframes ppRingSlide { from { background-position: 0 0; } to { background-position: 200% 0; } }
  @keyframes ppSpin { to { transform: rotate(360deg); } }
  @keyframes ppPulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
  @keyframes ppRise { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
  @keyframes ppSlideUp { from { transform: translateY(100%); } to { transform: none; } }
  .pp-ring { animation: ppRingSlide 6s linear infinite; }
  .pp-spin { animation: ppSpin 6s linear infinite; }
  .pp-pulse { animation: ppPulse 1.6s ease-in-out infinite; }
  .pp-rise { animation: ppRise 420ms cubic-bezier(0,0,.2,1) both; }
  .pp-btn { font-family: inherit; cursor: pointer; text-transform: uppercase; transition: background-color .2s, color .2s; box-sizing: border-box; }
  .pp-btn:disabled { opacity: .5; cursor: default; }
  .pp-primary { background: #fff; color: #000; border: 2px solid #fff; }
  .pp-primary:hover:not(:disabled) { background: #000; color: #fff; }
  .pp-primary-cyan:hover:not(:disabled) { color: ${CYAN}; }
  .pp-outline { background: transparent; color: #fff; border: 2px solid #fff; }
  .pp-outline:hover:not(:disabled) { background: #fff; color: #000; }
  .pp-red { background: transparent; color: ${RED}; border: 2px solid ${RED}; }
  .pp-red:hover:not(:disabled) { background: ${RED}; color: #fff; }
  .pp-green { background: ${GREEN}; color: #000; border: 2px solid ${GREEN}; }
  .pp-green:hover:not(:disabled) { background: #000; color: ${GREEN}; }
  .pp-black { background: #000; color: #fff; border: 2px solid #fff; }
  .pp-black:hover:not(:disabled) { background: #fff; color: #000; }
  .pp-tile { text-decoration: none; color: #fff; border: 2px solid #fff; background: #000; transition: background-color .2s, color .2s; }
  .pp-tile:hover { background: #fff; color: #000; }
  .pp-page a { color: #fff; }
  .pp-page textarea, .pp-page input { font-family: inherit; }
  .pp-page textarea:focus, .pp-page input:focus { outline: none; box-shadow: none; }
  .pp-page input::placeholder, .pp-page textarea::placeholder { color: rgba(255,255,255,.4); }
  @media (prefers-reduced-motion: reduce) {
    .pp-ring, .pp-spin, .pp-pulse, .pp-rise { animation: none !important; }
  }
`;

export function PortalPageStyles() {
  return <style>{PORTAL_PAGE_CSS}</style>;
}

/** The 4px band of the Portal ring's colours under the white navbar, sliding. */
export function RingBar() {
  return (
    <div
      aria-hidden
      className="pp-ring"
      data-testid="portal-ring-bar"
      style={{ height: 4, background: RING, backgroundSize: '200% 100%' }}
    />
  );
}

/**
 * The Portal's own picture, blurred, behind everything — then a scrim down to the viewer's
 * ground, then CRT scanlines. Every Portal page looks different because of this layer.
 */
export function Backdrop({ url }) {
  return (
    <>
      {url && (
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: -80,
            // Quoted: a stored filename can carry spaces and commas, which an unquoted url() rejects.
            backgroundImage: `url("${String(url).replace(/["\\]/g, '\\$&')}")`, backgroundSize: 'cover', backgroundPosition: 'center',
            filter: 'blur(48px) saturate(1.5)', opacity: 0.55,
          }}
        />
      )}
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(26,26,26,.35) 0%, rgba(26,26,26,.8) 55%, #1A1A1A 100%)' }} />
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(0deg, rgba(255,255,255,.03) 0 1px, transparent 1px 3px)', pointerEvents: 'none' }} />
    </>
  );
}

/**
 * One button, five looks. `kind`: primary (white) · outline · red · green · black.
 * `cyan` makes a primary button's hover text hyper-cyan (YOUR PASS TO THE PORTAL).
 */
export function Btn({ kind = 'outline', cyan = false, size = 'md', className = '', style, children, ...rest }) {
  const sizes = {
    lg: { fontSize: 13, letterSpacing: '0.14em', padding: 16, fontWeight: 700 },
    md: { fontSize: 12, letterSpacing: '0.14em', padding: 14, fontWeight: 700 },
    sm: { fontSize: 10, letterSpacing: '0.10em', padding: '9px 12px', fontWeight: 700 },
    xs: { fontSize: 10, letterSpacing: '0.10em', padding: '7px 10px', fontWeight: 700 },
  };
  return (
    <button
      type="button"
      className={`pp-btn pp-${kind} ${cyan ? 'pp-primary-cyan' : ''} font-mono ${className}`}
      style={{ ...sizes[size], ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Section header: `WANTS IN · 2` */
export function SectionLabel({ children }) {
  return (
    <div className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.75)' }}>
      {children}
    </div>
  );
}

/** A black panel with the 2px white rule. */
export function Box({ style, className = '', children, ...rest }) {
  return (
    <div className={className} style={{ border: '2px solid #fff', background: '#000', ...style }} {...rest}>
      {children}
    </div>
  );
}

/** Square avatar with the 2px white border. The only circles on the page are the ring art. */
export function SquareAvatar({ url, name, size = 36 }) {
  return (
    <span
      className="block flex-shrink-0 overflow-hidden"
      style={{ width: size, height: size, border: '2px solid #fff', boxSizing: 'border-box', background: '#000' }}
    >
      {url
        ? <img src={url} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
        : (
          <span className="flex h-full w-full items-center justify-center font-mono font-bold text-white" style={{ fontSize: Math.round(size * 0.36) }}>
            {(name || '?').charAt(0).toUpperCase()}
          </span>
        )}
    </span>
  );
}

// Lucide at stroke 2 with square caps, inline.
const g = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'square', strokeLinejoin: 'miter' };
export const PinIcon = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></svg>;
export const LockIcon = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><rect x="5" y="11" width="14" height="10" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>;
export const MutedIcon = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M22 9l-6 6M16 9l6 6" /></svg>;
export const SoundIcon = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 010 7M19 5a10 10 0 010 14" /></svg>;
export const PencilIcon = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M4 20h4L19 9l-4-4L4 16v4z" /></svg>;
export const RefreshIcon = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M4 4v6h6M20 20v-6h-6" /><path d="M20 10A8 8 0 006 5.3L4 10M4 14a8 8 0 0014 4.7l2-4.7" /></svg>;
export const CheckIcon = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g} strokeWidth={2.5}><path d="M4 12l5 5L20 6" /></svg>;
export const XIcon = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M5 5l14 14M19 5L5 19" /></svg>;
export const BackIcon = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" {...g}><path d="M14 5l-7 7 7 7" /></svg>;
